import { z } from 'zod';

/**
 * Per-user interface preferences.
 *
 * These exist because the people using this all day do not want the same thing. A
 * coordinator triaging a phone call wants to glance at a customer without losing the job
 * they are on; a manager reviewing history wants the whole page. Forcing one behaviour on
 * both is how an interface starts feeling like it is fighting you.
 *
 * Stored server-side against the user, not in the browser, so the choice follows them
 * between machines.
 */

/**
 * What happens when you click a record.
 *
 * - `page`  — navigate to it. Simple, and right when the record *is* the task.
 * - `peek`  — open it in a side panel over the current screen, which stays where it is.
 *             Right when the record is context for the task you are already doing.
 * - `tab`   — open a new browser tab. Right when you are comparing, or keeping a
 *             reference open while working elsewhere.
 *
 * Whatever the setting, the modifier keys always win for that one click (see
 * `resolveOpenMode`), because a per-click override is faster than changing a preference.
 */
export const RECORD_OPEN_MODES = ['page', 'peek', 'tab'] as const;
export type RecordOpenMode = (typeof RECORD_OPEN_MODES)[number];

/** Where the side panel appears. Left-handed users and dual-screen setups differ. */
export const PEEK_SIDES = ['right', 'left'] as const;
export type PeekSide = (typeof PEEK_SIDES)[number];

export const DENSITY_MODES = ['comfortable', 'compact'] as const;
export type DensityMode = (typeof DENSITY_MODES)[number];

/**
 * How far back a time-based list reaches when you open it.
 *
 * Every list here sits on a decade of imported history — 4 454 jobs, 2 736 quotes — and opening
 * one at "everything ever" costs a slow first page to show rows nobody is working on. So the
 * lists open on a window.
 *
 * It is a setting rather than a constant because the three businesses are not the same size:
 * Hydrokleen closes enough work in a week that seven days is a full view, while Mistral SAV's
 * quotes sit open for a month before anyone hears back, and a seven-day default there would
 * hide the entire pipeline. Whoever runs each office picks their own.
 *
 * `all` is deliberately offered. A window is a convenience, never a wall, and a person looking
 * for something from two years ago must be able to say so in one click.
 */
export const LIST_WINDOWS = ['7d', '30d', '90d', '365d', 'all'] as const;
export type ListWindow = (typeof LIST_WINDOWS)[number];

/** Days in each window, or null for "no limit". */
export const LIST_WINDOW_DAYS: Record<ListWindow, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
  all: null,
};

export const userPreferencesSchema = z.object({
  /** Default behaviour when clicking a record link. */
  recordOpenMode: z.enum(RECORD_OPEN_MODES).default('peek'),
  peekSide: z.enum(PEEK_SIDES).default('right'),
  /** Row height in lists. Coordinators who scan all day tend to want `compact`. */
  density: z.enum(DENSITY_MODES).default('comfortable'),
  /** Where signing in lands. Technicians are sent to the field app regardless of this. */
  landingRoute: z.string().max(120).default('/'),
  /** Show the timeline expanded by default on customer and job records. */
  timelineExpanded: z.boolean().default(true),
  locale: z.enum(['fr', 'en']).default('fr'),
  /**
   * The period a time-based list opens on — invoices, quotes, jobs, the journal.
   *
   * Ninety days as the shipped default: long enough to cover a full billing cycle plus the
   * chasing that follows it, short enough that the first page is work rather than history. Any
   * list applying it says so, and says how many rows it is holding back.
   *
   * Not applied to the customer directory. A customer is not an event, and "clients from the
   * last 90 days" is not a question anybody asks.
   */
  defaultListWindow: z.enum(LIST_WINDOWS).default('90d'),
});

export type UserPreferences = z.infer<typeof userPreferencesSchema>;

export const DEFAULT_PREFERENCES: UserPreferences = userPreferencesSchema.parse({});

/**
 * Turn a window into the two dates a list query actually carries.
 *
 * The resolution happens in the client, not the server, and the result goes into the URL as
 * plain `from` and `to`. That is the whole reason a shared link means the same thing to the
 * person who receives it: if the URL said `window=30d` the recipient would see thirty days from
 * *their* today, and a link sent on Friday about "the last thirty days" would answer a different
 * question by Monday. Dates do not drift.
 *
 * Returns null for `all`, which is the absence of a filter rather than a very wide one.
 */
export function resolveWindow(window: ListWindow, today: Date = new Date()): { from: string; to: string } | null {
  const days = LIST_WINDOW_DAYS[window];
  if (days === null) return null;

  const to = new Date(today);
  const from = new Date(today);
  // Inclusive of today, so a seven-day window is today plus the six before it rather than
  // today plus seven — which is what "les 7 derniers jours" means to the person reading it.
  from.setDate(from.getDate() - (days - 1));

  return { from: isoDay(from), to: isoDay(to) };
}

/**
 * `YYYY-MM-DD` in the reader's own timezone.
 *
 * `toISOString().slice(0, 10)` is the tempting one-liner and it is wrong here: at 20:00 in Laval
 * it already returns tomorrow's date in UTC, so a window built from it silently starts and ends
 * a day late every evening.
 */
export function isoDay(at: Date): string {
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${at.getFullYear()}-${month}-${day}`;
}

/**
 * Decide how one particular click should open a record.
 *
 * Modifier keys override the preference for that click, following the conventions people
 * already have from their browser and file manager, so nothing has to be learned:
 *
 *   ⌘ / Ctrl click  → new tab      (what a browser already does)
 *   Shift click     → full page    (escape a peek-by-default without changing settings)
 *   Alt click       → peek         (the reverse escape)
 *   middle click    → new tab      (browser convention)
 *
 * A plain click uses the preference.
 */
export function resolveOpenMode(
  preference: RecordOpenMode,
  event: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean; button?: number },
): RecordOpenMode {
  if (event.button === 1 || event.metaKey || event.ctrlKey) return 'tab';
  if (event.shiftKey) return 'page';
  if (event.altKey) return 'peek';
  return preference;
}

/** Record kinds the side panel knows how to render. */
export const PEEKABLE = ['customer', 'job', 'quote', 'invoice', 'appointment', 'payment'] as const;
export type Peekable = (typeof PEEKABLE)[number];

/**
 * The peek target as it appears in the URL: `customer:0d7f…`.
 *
 * In the URL rather than component state so a reload, a shared link and the back button all
 * restore the same panel — the same rule the rest of the interface follows.
 */
export function encodePeek(kind: Peekable, id: string): string {
  return `${kind}:${id}`;
}

export function decodePeek(raw: string | undefined | null): { kind: Peekable; id: string } | null {
  if (!raw) return null;
  const separator = raw.indexOf(':');
  if (separator < 1) return null;
  const kind = raw.slice(0, separator);
  const id = raw.slice(separator + 1);
  if (!PEEKABLE.includes(kind as Peekable) || !id) return null;
  return { kind: kind as Peekable, id };
}

/** Canonical full-page path for a record, so nothing invents its own. */
export function recordPath(kind: Peekable, id: string): string {
  switch (kind) {
    case 'customer':
      return `/customers/${id}`;
    case 'job':
      return `/jobs/${id}`;
    case 'quote':
      return `/quotes/${id}`;
    case 'invoice':
      return `/invoices/${id}`;
    case 'payment':
      return `/payments/${id}`;
    case 'appointment':
      // A visit has no page of its own; it is shown on the job it belongs to.
      return `/jobs?appointment=${id}`;
  }
}
